import { useState, useEffect } from 'react';
import { confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import { auth } from '../firebase';
import LeafLogo from '../components/LeafLogo';
import styles from './LoginPage.module.css';

export default function ResetPasswordPage() {
  const [oobCode,   setOobCode]   = useState('');
  const [email,     setEmail]     = useState('');
  const [password,  setPassword]  = useState('');
  const [confirm,   setConfirm]   = useState('');
  const [error,     setError]     = useState('');
  const [loading,   setLoading]   = useState(false);
  const [done,      setDone]      = useState(false);
  const [invalid,   setInvalid]   = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('oobCode');
    if (!code) { setInvalid(true); return; }
    setOobCode(code);
    verifyPasswordResetCode(auth, code)
      .then(em => setEmail(em))
      .catch(() => setInvalid(true));
  }, []);

  const handleReset = async () => {
    setError('');
    if (!password) { setError('Please enter a new password.'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters.'); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    setLoading(true);
    try {
      await confirmPasswordReset(auth, oobCode, password);
      setDone(true);
    } catch (err) {
      if (err.code === 'auth/expired-action-code') {
        setError('This reset link has expired. Please request a new one.');
      } else if (err.code === 'auth/weak-password') {
        setError('Password must be at least 6 characters.');
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.root}>

      {/* ── Left Panel ── */}
      <div className={styles.left}>
        <div className={styles.logoWrap}>
          <div className={styles.logoCircle}>
            <LeafLogo size={80} color="#FFFFFF" bgColor="#7B3F1E" />
          </div>
          <div className={styles.brandName}>
            Juan Café
            <span>Supply Chain Management</span>
          </div>
        </div>
        <p className={styles.tagline}>
          Streamlining supply ordering across all branches of Top Juan Franchising Inc.
        </p>
        <div className={styles.decorCircle1} />
        <div className={styles.decorCircle2} />
      </div>

      {/* ── Right Panel ── */}
      <div className={styles.right}>

        {invalid ? (
          <>
            <div className={styles.formHead}>
              <h2>Link invalid</h2>
              <p>This password reset link is invalid or has already been used.</p>
            </div>
            <a href="/" className={styles.loginBtn} style={{ textAlign: 'center', textDecoration: 'none', display: 'block' }}>
              Back to Sign In
            </a>
          </>
        ) : done ? (
          <>
            <div className={styles.formHead}>
              <h2>Password updated</h2>
              <p>Your password has been reset successfully. You can now sign in.</p>
            </div>
            <a href="/" className={styles.loginBtn} style={{ textAlign: 'center', textDecoration: 'none', display: 'block' }}>
              Go to Sign In
            </a>
          </>
        ) : (
          <>
            <div className={styles.formHead}>
              <h2>Set new password</h2>
              <p>{email ? <>For <strong>{email}</strong></> : 'Enter your new password below.'}</p>
            </div>

            {error && (
              <div className={styles.errorBanner}>
                <span>⚠</span> {error}
              </div>
            )}

            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel} htmlFor="new-password">New Password</label>
              <input
                id="new-password"
                className={`${styles.input} ${error ? styles.inputError : ''}`}
                type="password"
                placeholder="At least 6 characters"
                value={password}
                onChange={e => setPassword(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleReset()}
                autoFocus
                autoComplete="new-password"
              />
            </div>

            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel} htmlFor="confirm-password">Confirm Password</label>
              <input
                id="confirm-password"
                className={`${styles.input} ${error ? styles.inputError : ''}`}
                type="password"
                placeholder="Re-enter your new password"
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleReset()}
                autoComplete="new-password"
              />
            </div>

            <button
              className={styles.loginBtn}
              onClick={handleReset}
              disabled={loading}
              type="button"
            >
              {loading ? 'Saving…' : 'Save New Password'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
